import * as path from "node:path";
import { runAz } from "./az.js";
import type { ClassifiedResult, ClassifiedChange } from "./whatif-noise.js";

// Structured ARM What-If result (from `az deployment group what-if --no-pretty-print -o json`).
export interface WhatIfPropertyChange {
  path: string;
  propertyChangeType: string; // Modify | Create | Delete | Array | NoEffect | NoChange
  before?: unknown;
  after?: unknown;
  children?: WhatIfPropertyChange[];
}
export interface WhatIfChange {
  resourceId: string;
  changeType: string; // Modify | Unsupported | NoChange | Ignore | Create | Delete
  delta?: WhatIfPropertyChange[];
  unsupportedReason?: string;
}
export interface WhatIfResult { changes?: WhatIfChange[] }

/**
 * Derive the ARM resource type ("Microsoft.X/type[/subtype]") from a resourceId.
 * Returns '' for an ARM-expression resourceId (Unsupported changes carry an
 * `[extensionResourceId(...)]` expression, not a normal id).
 */
export function deriveResourceType(resourceId: string): string {
  if (resourceId.startsWith("[")) return "";
  const afterProviders = resourceId.split("/providers/")[1];
  if (!afterProviders) return "";
  const segs = afterProviders.split("/");
  const parts = [segs[0]]; // namespace
  for (let i = 1; i < segs.length; i += 2) parts.push(segs[i]); // type segments at odd indices
  return parts.join("/");
}

/** Run `az deployment group what-if --no-pretty-print` and return the changes. Read-only. */
export async function runWhatIf(opts: { resourceGroup: string; repoRoot: string; params: string[] }): Promise<WhatIfChange[]> {
  const bicepPath = path.join(opts.repoRoot, "deploy", "main.bicep");
  const out = await runAz([
    "deployment", "group", "what-if",
    "--resource-group", opts.resourceGroup,
    "--template-file", bicepPath,
    "--parameters", ...opts.params,
    "--no-pretty-print",
    "--output", "json",
  ]);
  const result = JSON.parse(out) as WhatIfResult;
  return result.changes ?? [];
}

const fmt = (c: ClassifiedChange) =>
  `  • ${c.resourceType} ${c.path}: ${JSON.stringify(c.before ?? null)} → ${JSON.stringify(c.after ?? null)}${c.reason ? `   [${c.reason}]` : ""}`;

/** Build the what-if report + exit code. exitCode 0 ⇔ nothing unexpected. */
export function reportWhatIf(r: ClassifiedResult, opts: { json: boolean }): { output: string; exitCode: number } {
  const exitCode = r.unexpected.length === 0 ? 0 : 1;
  if (opts.json) {
    return { output: JSON.stringify({ summary: { noise: r.noise.length, pending: r.pending.length, unexpected: r.unexpected.length }, pending: r.pending, unexpected: r.unexpected, exitCode }, null, 2), exitCode };
  }
  const lines: string[] = [];
  const total = r.noise.length + r.pending.length + r.unexpected.length;
  lines.push(`what-if: ${total.toString()} changes — ${r.noise.length.toString()} known-noise (dropped), ${r.pending.length.toString()} known-pending, ${r.unexpected.length.toString()} un-catalogued`);
  if (r.pending.length) {
    lines.push("", "known-pending (expected, will apply):");
    for (const c of r.pending) lines.push(fmt(c));
  }
  if (r.unexpected.length) {
    lines.push("", "⚠ UN-CATALOGUED (review before any apply):");
    for (const c of r.unexpected) lines.push(fmt(c));
    lines.push("", `✗ ${r.unexpected.length.toString()} un-catalogued change(s) — not safe to apply blindly.`);
  } else {
    lines.push("", "✓ only known noise + expected pending — safe to review for an approved apply.");
  }
  return { output: lines.join("\n"), exitCode };
}
